// Shared helpers for "server-side export" endpoints. The staff backend builds
// the file (CSV/PDF), uploads it to S3, and returns a presigned download URL in
// the standard envelope. Every download hook used to re-implement the same
// envelope parsing by hand; this centralises it so the knowledge of "where the
// URL lives in the response" has a single home.

import { useMutation } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";
import type { AxiosRequestConfig } from "axios";

import { getPrivateInstance } from "./_privateInstance";

/** Fields a presigned-download endpoint may return. Only `download_url` is
 * guaranteed; the rest are surfaced when the endpoint provides them. */
export type PresignedDownloadData = {
  download_url: string;
  file_name?: string;
  row_count?: number;
  truncated?: boolean;
  s3_key?: string;
  s3_bucket?: string;
  expires_in?: number;
};

/** `null` signals "no usable download URL" so callers can bail without a throw. */
export type PresignedDownloadResponse = PresignedDownloadData | null;

type DownloadEnvelopeBody = Partial<PresignedDownloadData> &
  Record<string, unknown>;

type DownloadEnvelope = {
  data?: DownloadEnvelopeBody & { data?: DownloadEnvelopeBody };
};

/**
 * Pull the presigned-download payload out of an axios response. Most endpoints
 * nest the payload under `data.data`; some (e.g. fraud flagged-users) return it
 * at `data`, so both are checked. Returns `null` when there is no
 * `download_url`. Only keys actually present on the body are copied, so a
 * `{ download_url, file_name }` response stays shaped exactly like that.
 */
export const extractDownloadData = (
  response: unknown,
): PresignedDownloadResponse => {
  const envelope = response as DownloadEnvelope | undefined;
  const body = envelope?.data?.data ?? envelope?.data;
  if (!body?.download_url) return null;

  const result: PresignedDownloadData = {
    download_url: body.download_url as string,
  };
  if (body.file_name !== undefined) result.file_name = body.file_name as string;
  if (body.row_count !== undefined) result.row_count = body.row_count as number;
  if (body.truncated !== undefined)
    result.truncated = body.truncated as boolean;
  if (body.s3_key !== undefined) result.s3_key = body.s3_key as string;
  if (body.s3_bucket !== undefined)
    result.s3_bucket = body.s3_bucket as string;
  if (body.expires_in !== undefined)
    result.expires_in = body.expires_in as number;
  return result;
};

/**
 * Build a `useMutation` hook for a GET-based presigned download endpoint. The
 * staff export endpoints all share the same shape — `GET {route}?{filters}`
 * returning the presigned-download envelope — so the only per-endpoint inputs
 * are the route and a stable mutation key. Filters are passed straight through
 * as query params; callers own building/typing that payload.
 */
export const createPresignedDownloadHook =
  <TParams extends Record<string, unknown>>(
    route: string,
    mutationKey: string,
    requestConfig: Pick<AxiosRequestConfig, "paramsSerializer"> = {},
  ): (() => UseMutationResult<PresignedDownloadResponse, Error, TParams>) =>
  () =>
    useMutation<PresignedDownloadResponse, Error, TParams>({
      mutationKey: [mutationKey],
      mutationFn: async (params) =>
        extractDownloadData(
          await getPrivateInstance().get(route, { params, ...requestConfig }),
        ),
    });
